import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { Building2, ExternalLink, X } from "lucide-react";
import { cardAttention } from "../common/cardAttention.css";
import { ContextButton } from "../../../ui/components/ListView/components/ContextButton";
import type { ContextButtonAction as ResourceCardMenuAction } from "../../../ui/components/ListView/components/ContextButton";
import { useI18n } from "../../lib/i18n";
import * as styles from "./OrganizationCard.css";

export interface RoleChip {
  name: string;
  color?: string | null;
}

export interface OrganizationRoleOption {
  id: string;
  name: string;
  color?: string | null;
}

export interface OrganizationCardProps {
  id: string;
  name: string;
  description?: string | null;
  logoUrl?: string | null;
  city?: string | null;
  state?: string | null;
  roles?: RoleChip[];
  isMember?: boolean;
  isPendingRequest?: boolean;
  isPreApproved?: boolean;
  onRequestMembership?: () => void;
  onInstantJoin?: () => void;
  onCancelMembershipRequest?: () => void;
  compact?: boolean;
  menuActions?: ResourceCardMenuAction[];
  menuDisabled?: boolean;
  /** Membership status label (e.g. active member vs pending request vs email invite). */
  membershipStatus?: "active" | "pending" | "invited";
  /** Active member org role (for admin role dropdown). */
  roleId?: string | null;
  orgRoleOptions?: OrganizationRoleOption[];
  onRoleChangeRequest?: (roleId: string) => void;
  roleDropdownDisabled?: boolean;
  roleDropdownDisabledTitle?: string;
  /** When set, overrides view-link visibility; otherwise shown when isMember is true. */
  showViewOrganization?: boolean;
}

function formatRoleLabel(role: string): string {
  return role
    .replace(/^pro:/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function OrganizationCard({
  id,
  name,
  description,
  logoUrl,
  city,
  state,
  roles,
  isMember,
  isPendingRequest,
  isPreApproved,
  onRequestMembership,
  onInstantJoin,
  onCancelMembershipRequest,
  compact,
  menuActions = [],
  menuDisabled = false,
  membershipStatus,
  roleId,
  orgRoleOptions = [],
  onRoleChangeRequest,
  roleDropdownDisabled = false,
  roleDropdownDisabledTitle,
  showViewOrganization,
}: OrganizationCardProps) {
  const navigate = useNavigate();
  const { t } = useI18n();

  const locationStr = [city, state].filter(Boolean).join(", ");

  const roleChip = roles?.[0];
  const showRoleDropdown =
    membershipStatus === "active" &&
    orgRoleOptions.length > 0 &&
    Boolean(onRoleChangeRequest);
  const showRoleChip = !showRoleDropdown && Boolean(roleChip) && !isPendingRequest;
  const showJoinAttention =
    Boolean(isPreApproved && !isMember && !isPendingRequest && onInstantJoin);
  const showViewLink = showViewOrganization ?? Boolean(isMember);

  const handleRoleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextRoleId = e.target.value;
    if (nextRoleId && nextRoleId !== (roleId ?? "")) {
      onRoleChangeRequest?.(nextRoleId);
    }
    e.target.value = roleId ?? "";
  };

  return (
    <div className={clsx(styles.card, showJoinAttention && cardAttention)}>
      <div className={styles.logo}>
        {logoUrl ? (
          <img src={logoUrl} alt="" className={styles.logoImg} />
        ) : (
          <Building2 size={20} color="#9CA3AF" />
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.nameRow}>
          <p className={styles.name}>{name}</p>
        </div>
        {description && !compact && (
          <p className={styles.description}>{description}</p>
        )}
        {locationStr && (
          <p className={styles.location}>{locationStr}</p>
        )}
        {isPreApproved && !isMember && !isPendingRequest && !compact && (
          <p className={styles.approvedHint}>
            You have been invited to join
          </p>
        )}
      </div>

      <div className={styles.actions}>
        {isPreApproved && !isMember && !isPendingRequest && onInstantJoin && (
          <button
            type="button"
            className={styles.joinNowButton}
            onClick={(e) => {
              e.stopPropagation();
              onInstantJoin();
            }}
          >
            Join now
          </button>
        )}
        {isMember === false &&
          !isPendingRequest &&
          !isPreApproved &&
          onRequestMembership && (
          <button
            type="button"
            className={styles.requestButton}
            onClick={(e) => {
              e.stopPropagation();
              onRequestMembership();
            }}
          >
            Request access
          </button>
        )}
        {membershipStatus === "active" && !isPendingRequest && (
          <span className={`${styles.chip} ${styles.chipActive}`}>
            Active
          </span>
        )}
        {membershipStatus === "invited" && (
          <span className={`${styles.chip} ${styles.chipInvited}`}>
            Invited
          </span>
        )}
        {(isPendingRequest || membershipStatus === "pending") && (
          <span className={`${styles.chip} ${styles.chipPending}`}>
            Pending approval
          </span>
        )}
        {isPendingRequest && onCancelMembershipRequest && !membershipStatus && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={(e) => {
              e.stopPropagation();
              onCancelMembershipRequest();
            }}
            aria-label={t("pro.orgs.cancelMembershipRequest")}
            title={t("pro.orgs.cancelMembershipRequest")}
          >
            <X size={14} />
          </button>
        )}
        {showRoleDropdown && (
          <select
            className={styles.roleSelect}
            value={roleId ?? ""}
            disabled={menuDisabled || roleDropdownDisabled}
            onChange={handleRoleSelect}
            aria-label={`Role in ${name}`}
            title={
              roleDropdownDisabled ? roleDropdownDisabledTitle : undefined
            }
            style={
              roleChip?.color
                ? {
                    backgroundColor: `${roleChip.color}18`,
                    color: roleChip.color,
                    borderColor: `${roleChip.color}40`,
                  }
                : undefined
            }
          >
            {orgRoleOptions.map((role) => (
              <option key={role.id} value={role.id}>
                {formatRoleLabel(role.name)}
              </option>
            ))}
          </select>
        )}
        {showRoleChip && (
          <span
            className={styles.chip}
            style={roleChip!.color ? {
              backgroundColor: `${roleChip!.color}18`,
              color: roleChip!.color,
            } : undefined}
          >
            {formatRoleLabel(roleChip!.name)}
          </span>
        )}
        {menuActions.length > 0 && (
          <ContextButton
            actions={menuActions}
            disabled={menuDisabled}
            ariaLabel={`Actions for ${name}`}
          />
        )}
        {showViewLink && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={(e) => {
              e.stopPropagation();
              navigate({ to: "/workspace/organizations/$orgId", params: { orgId: id } });
            }}
            aria-label={`View ${name}`}
            title={t("pro.orgs.viewOrganization")}
          >
            <ExternalLink size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
